import React, { useState, useEffect } from "react";
import { Modal } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { useSelector, useDispatch } from "react-redux";
import { review } from "../../store";
import { useToast } from "../toaster/toaster";
import "./ratingPopup.css";

const RatingPopup = ({
  isOpen,
  onClose,
  onSuccess,
  coachData,
  existingReview = null,
}) => {
  const dispatch = useDispatch();
  const toast = useToast();
  const { isLoading } = useSelector((state) => state.review);

  const isEditMode = !!existingReview;

  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [reviewText, setReviewText] = useState("");
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setRating(existingReview?.rating || 0);
      setReviewText(existingReview?.review || "");
      setHoveredRating(0);
      setSubmitted(false);
      return;
    }

    setRating(0);
    setHoveredRating(0);
    setReviewText("");
    setSubmitted(false);
  }, [isOpen, existingReview]);

  const handleSubmit = async () => {
    if (!rating || rating === 0) {
      toast.error("Please select a rating", "Validation Error");
      return;
    }

    if (!coachData?.id) {
      toast.error("Coach information is missing", "Validation Error");
      return;
    }

    try {
      const payload = {
        coachId: coachData.id,
        rating,
        review: reviewText.trim(),
      };

      if (isEditMode) {
        await dispatch(review.updateReview(payload)).unwrap();
      } else {
        await dispatch(review.submitReview(payload)).unwrap();
      }

      setSubmitted(true);
      toast.success(
        isEditMode ? "Your review has been updated!" : "Thank you for your feedback!",
        "Success",
      );

      if (onSuccess) {
        onSuccess();
      }

      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      toast.error(error.message || "Failed to submit review", "Error");
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const displayRating = hoveredRating || rating;

  return (
    <Modal
      open={isOpen}
      onCancel={handleClose}
      footer={null}
      centered
      width={480}
      className="rating-popup"
      closable={!isLoading}
      closeIcon={<CloseOutlined />}
    >
      <div className="rating-popup-content">
        {!submitted ? (
          <>
            <div className="rating-coach-info">
              <img
                src={coachData?.picture || "/default-avatar.png"}
                alt={coachData?.name || "Coach"}
                className="rating-coach-avatar"
                onError={(e) => {
                  e.target.src = "/default-avatar.png";
                }}
              />
              <div className="rating-coach-details">
                <h3 className="rating-coach-name">
                  {coachData?.name || "Coach"}
                </h3>
                <p className="rating-topic-name">
                  {isEditMode ? "Update your review" : "Share your experience"}
                </p>
              </div>
            </div>

            <div className="rating-section">
              <p className="rating-label">How was your session?</p>
              <div className="star-rating">
                {[1, 2, 3, 4, 5].map((star) => (
                  <span
                    key={star}
                    className={`material-symbols-outlined star ${
                      star <= displayRating ? "active" : ""
                    }`}
                    onClick={() => setRating(star)}
                    onMouseEnter={() => setHoveredRating(star)}
                    onMouseLeave={() => setHoveredRating(0)}
                  >
                    {star <= displayRating ? "star" : "star_border"}
                  </span>
                ))}
              </div>
            </div>

            <div className="review-section">
              <p className="review-label">Leave a review (optional)</p>
              <textarea
                className="review-textarea"
                placeholder="Share your experience..."
                value={reviewText}
                onChange={(e) => {
                  if (e.target.value.length <= 500) {
                    setReviewText(e.target.value);
                  }
                }}
                rows={4}
                maxLength={500}
              />
              <p className="character-count">{reviewText.length}/500</p>
            </div>

            <button
              className="rating-submit-btn"
              onClick={handleSubmit}
              disabled={isLoading || rating === 0}
            >
              {isLoading
                ? isEditMode
                  ? "Updating..."
                  : "Submitting..."
                : isEditMode
                  ? "Update Review"
                  : "Submit Review"}
            </button>
          </>
        ) : (
          <div className="rating-success">
            <span className="material-symbols-outlined success-icon">
              check_circle
            </span>
            <h3>Thank You!</h3>
            <p>Your feedback helps others find the right coach.</p>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default RatingPopup;
